import { IPublicAttributeInfo, OWDataType } from "@multiweb/types/contracts";
import { AdditionalOrdering, ConditionParams, IDataSource, IKeyValueItem, IOrderBy, IUseDistinctDataSource, OrderType } from "../../../../../../Global/Base/DataSources/Interfaces";
import { CancellationToken, CancellationTokenSource } from "../../../../../../Global/Base/Promises/CancellationTokenSource";
import { PromiseUtils } from "../../../../../../Global/Base/Promises/PromiseUtils";
import { ModelDescriptionsManager } from "../../../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { Control } from "../../../../../UI.BaseControls/Control";
import { Edit } from "../../../../../UI.BaseControls/Edit";
import { Column } from "../../../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory } from "../../../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { CheckedDataRow } from "../../../../../UI.Controls/DataGridSubcontrols/Components/CheckedDataRow";
import { DBDataSource } from "../../../../../UI.Controls/DataSource/DBDataSource";
import { IDataGridSelectionChangedEvent } from "../../../../../UI.Controls/IEvent";
import { ValueListDataGrid } from "../../../../../UI.Controls/ValueListDataGrid";
import { createElement, registerCustomElement } from "../../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { CaseInsensitiveMap } from "../../../../../UI.React/types/multiweb";
import { FrameContext } from "../../../UIManagerInterfaces";
import { FilterRegistrations } from "../FilterRegistrations";
import { logger } from "../logger";

export class ValueList extends Control {
    private _dataGrid: ValueListDataGrid;
    private _dataSource: IDataSource;
    private _searchBox: Edit;
    private _dgContainer: HTMLElement;
    private _attrDataTypes: CaseInsensitiveMap<string>;

    private _className: string;
    private _attrs: Array<string>;
    private _selectedValues: Array<IKeyValueItem> = [];
    private _afterOpenValues: Array<IKeyValueItem> = [];
    private _progressBarLoader: HTMLElement;
    private _frameCondition: string;
    private _frameConditionParams?: ConditionParams;

    /** Vybrané hodnoty */
    public get selectedValues(): Array<IKeyValueItem> {
        return this._selectedValues;
    }

    /** Vybrané hodnoty */
    public set selectedValues(values: Array<IKeyValueItem>) {
        this._selectedValues = [];
        if (values && values.length > 0) {
            this._selectedValues = Array.from(values);
            this._afterOpenValues = Array.from(values);
        }
        this._reloadAfterExternalSelectedValueChange();
    }

    /**
     * Odstraní hodnotu z kolekce vybraných hodnot
     * @param key Klíč hodnoty, která se má odstranit
     */
    public removeSelectedValue(key: string): void {
        if (this._removeSelectedValue(key)) {
            this._reloadAfterExternalSelectedValueChange();
        }
    }

    private _reloadAfterExternalSelectedValueChange(): void {
        if (this._selectedValues && this._selectedValues.length > 0) {
            this._setConditionAndLoad(`ID <> 0 OR ${this._attrs[0]} ∈ { '${this._selectedValues.map(k => k.key).join("', '")}' }`);
        } else {
            this._setConditionAndLoad("1=1");
        }
    }

    private _addSelectedValue(item: IKeyValueItem): void {
        const selectedValIdx = this._selectedValues.findIndex(i => i.key === item.key);
        if (selectedValIdx === -1)
            this._selectedValues.push(item);
    }

    private _removeSelectedValue(key: string): boolean {
        const selectedValIdx = this._selectedValues.findIndex(i => i.key === key);
        if (selectedValIdx >= 0) {
            this._selectedValues.splice(selectedValIdx, 1);
            return true;
        }
        return false;
    }

    public cancel(): void {
        this._selectedValues = Array.from(this._afterOpenValues);
    }
    protected _dgDataShownListener = () => {
        this._selectedDataItem(this._selectedValues);
    };

    public async initialize(className: string, attributes: Array<string>, frameContext: FrameContext, frameCondition?: string, frameConditionParams?: ConditionParams, disableValueShortening?: boolean, attrDataTypes?: CaseInsensitiveMap<string>): Promise<void> {
        const dataSource = new DBDataSource({
            className,
            attributes,
            condition: frameCondition,
            conditionParams: frameConditionParams,
            batchSize: this._dataGrid.fetchCount,
            dataLength: disableValueShortening ? -1 : 0,
            frameContext,
        });

        if (attributes.length > 0)
            dataSource.orderBy = attributes.map((item) => {
                return { attributeName: item, order: OrderType.ascending, additionalOrdering: AdditionalOrdering.linguistic } as IOrderBy;
            });/**/// [{ attributeName: attributes[0], order: OrderType.ascending } as IOrderBy]
        this._className = className;
        this._attrDataTypes = attrDataTypes;
        return this.initializeByDS(dataSource, attributes, frameCondition, frameConditionParams);
    }

    public async initializeByDS(dataSource: IDataSource, attributes: Array<string> = dataSource.getAttrs(), frameCondition?: string, frameConditionParams?: ConditionParams): Promise<void> {
        this._frameCondition = frameCondition;
        this._frameConditionParams = frameConditionParams;
        this._dataSource = dataSource;
        this._attrs = attributes;
        if (this._dataSource instanceof IUseDistinctDataSource)
            this._dataSource.useDistinct = true;
        this._dataGrid.dataSource = this._dataSource;
        this._dgContainer.appendChild(this._dataGrid);

        await this._createColumns();
        await this._dataSource.loadNewData();

        this._dataGrid.addEventListener("DataShown", this._dgDataShownListener);
    }
    public async changeFrameConditionAndLoad(frameCondition: string): Promise<void> {
        this._frameCondition = frameCondition;
        await this._dataSource.loadNewData();
    }
    protected async _createColumns(): Promise<any> {
        const columns = new Array<Column>();
        let attrs = new Array<string>();

        try {
            let attrInfos: ReadonlyArray<IPublicAttributeInfo>;
            if (!this._className && this._dataSource) { //Pokud má datasource neperzistetní data (např. ArrayDataSource), vytvoří se
                attrInfos = this._dataSource.getAttrs().map(i => ({ attributeName: i, type: OWDataType.string } as IPublicAttributeInfo));
            } else {
                if (this._attrDataTypes) {
                    attrInfos = this._attrs.map(i => {
                        return { attributeName: i, type: this._attrDataTypes.get(i) };
                    }) as IPublicAttributeInfo[];
                } else {
                    attrInfos = await ModelDescriptionsManager.instance.getAttributesInfo(this._className, this._attrs);
                }
            }
            for (const attrInfo of attrInfos) {
                const col: Column = ColumnFactory.initColumn({ attrName: attrInfo.attributeName, dataType: "attribute" }, attrInfo.type);
                if (!col.visible) {
                    continue;
                }

                columns.push(col);
                attrs = attrs.concat(col.dataSourceAttrNames);

                this._dataGrid.appendChild(col);
            }
            this._dataGrid.columns = columns;
            this._dataSource.setAttrs(attrs);
            this._dataSource.loadNewData();
        } finally {
            // Prazdne finally
        }
    }

    /**
     * Nastaví podmínku a načte nová data
     * @param cond podminka
     */
    protected async _setConditionAndLoad(condition: string): Promise<void> {
        let newCondition;
        if (this._frameCondition && condition)
            newCondition = `(${this._frameCondition}) AND(${condition})`;
        else if (this._frameCondition)
            newCondition = this._frameCondition;
        else if (condition)
            newCondition = condition;
        else
            newCondition = undefined;
        this._dataSource.condition = newCondition;
        if (this._frameConditionParams)
            this._dataSource.conditionParams = this._frameConditionParams;
        this._dataSource.loadNewData(false);
    }

    private _selectedDataItem(values: Array<IKeyValueItem>): Array<CheckedDataRow> {
        if (!values || values.length == 0)
            return [];
        const items = Array.from(this
            .querySelectorAll(values
                .map(item => `.CheckedDataRow[datakey='${item.key}']`) //`
                .join(","))) as CheckedDataRow[];

        for (const item of items) {
            item.isSelected = true;
        }

        return items;
    }

    protected override _constructor(): void {
        super._constructor();

        this.classList.add("ValueList", "dataGrid");

        const topContainer = createElement({ classes: "topContainer" });
        this._dgContainer = createElement({ classes: "dgContainer" });
        this._progressBarLoader = this._createLoader();
        this._dgContainer.appendChild(this._progressBarLoader);

        this._searchBox = new Edit();

        let cts: CancellationTokenSource;
        this._searchBox.afterAttach(() => {
            this._searchBox.registerEventListener("keyup", () => {
                this._showLoader();
                let promise: Promise<void>;
                ({ promise, cts } = PromiseUtils.cancelPrevAndDelayCall(cts, 500, (token?: CancellationToken) => {
                    let dshowed: any;
                    this._dataGrid.addEventListener("DataShown",
                        dshowed = () => {
                            this._selectedDataItem(this._selectedValues);
                            this._dataGrid.removeEventListener("DataShown", dshowed);
                            this._hideLoader();
                        });

                    const condition = `:Linguistic.LIKE(${this._attrs[0]}, '{value}%')`;
                    this._dataSource.setFilterCondition(FilterRegistrations.updateCondition(condition, { "value": this._searchBox.value }));
                    this._dataSource.loadNewData(false, token);
                }));
                promise.catch((e) => {
                    this._hideLoader();
                    PromiseUtils.cancellationCatch(e);
                });
            });
        });

        this._dataGrid = new ValueListDataGrid();

        topContainer.appendChild(this._searchBox);

        this.appendChild(topContainer);
        this.appendChild(this._dgContainer);
    }
    private _createLoader(): HTMLElement {
        const loaderElement = createElement({ type: "div", classes: "loader indeterminate-loader" });
        loaderElement.appendChild(createElement({ type: "div", classes: "bar" }));
        return loaderElement;
    }
    private _showLoader(): void {
        this._progressBarLoader.classList.toggle("loading", true);
    }
    private _hideLoader(): void {
        this._progressBarLoader.classList.toggle("loading", false);
    }
    protected override connectedCallback(): void {
        super.connectedCallback();

        this._dataGrid.addEventListener("SelectionChanged", (evt: IDataGridSelectionChangedEvent) => {
            const isRow = evt.target instanceof CheckedDataRow;
            if (!isRow)
                return;
            const rowId = evt.detail.data.clickedRow ? evt.detail.data.clickedRow.dataKey : -1;

            if (rowId == -1) {
                logger.warn("RowId undefine selectionChanged not called");
                return;
            }
            const item = evt.target as CheckedDataRow;
            //aktualizace pole _selectedValues na základě výběru uživatele
            if (!item.isSelected) {
                this._removeSelectedValue(item.dataKey);
            } else if (item.isSelected) {
                const keyValueItem = item.isSomeValueShortened ?
                    { key: item.dataKey, value: item.values, shortenedValue: item.isSomeValueShortened } as IKeyValueItemExtended
                    : { key: item.dataKey, value: item.values } as IKeyValueItem;
                this._addSelectedValue(keyValueItem);
            }
        });
    }
}

/**
 * Základní struktura pro klíč hodnota, rozšířená o informaci, jestli byla hodnota zkrácena.
 */
export interface IKeyValueItemExtended extends IKeyValueItem {
    shortenedValue: boolean;
}

export class IKeyValueItemExtended {
    private static _properties: string[] = ["key", "value", "shortenedValue"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return typeof (instance) === "object" && instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

registerCustomElement("tsc-ValueList", ValueList);
